import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import {
  TbBuildingBank,
  TbEye,
  TbEyeOff,
  TbInfoCircle,
  TbRefresh,
} from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import Tooltip from "../../Atoms/Tooltip";
import { formatCurrency } from "../../../Utils/formatters";
import { useAuth } from "../../../Contexts/AuthContext";
import { IB_ACTIVE_ACCOUNT_CHANGED_EVENT } from "../../../Constants/accountTypes";
import { getPixTransactionalLimits } from "../../../Services/pixLimitsApi";
import { resolveLimitProgress } from "../../../Pages/Private/Pix/PixLimits/constants";

const ZERO_MASK = formatCurrency(0);
const EMPTY_MONEY = new Set(["", "—", "–", "-"]);

const displayMoney = (showBalance, masked, formatted) => {
  if (!showBalance) return masked;
  const raw = String(formatted ?? "").trim();
  if (EMPTY_MONEY.has(raw)) return ZERO_MASK;
  return raw;
};

/** Tone badges as Tailwind utilities (utilities layer beats @layer components). */
const ICON_TONE_CLASS = {
  available:
    "home-balance-wallet__icon--available bg-brand-cream text-brand-gold-mid dark:bg-brand-dark-surface dark:text-brand-gold-mid",
  blocked:
    "home-balance-wallet__icon--blocked bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400",
  future:
    "home-balance-wallet__icon--future bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400",
  total:
    "home-balance-wallet__icon--total bg-slate-100 text-slate-700 dark:bg-slate-800/80 dark:text-slate-300",
  limitAvailable:
    "home-balance-wallet__icon--limit-available bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  limit:
    "home-balance-wallet__icon--limit bg-brand-cream text-brand-gold-mid dark:bg-brand-dark-surface dark:text-brand-gold-mid",
};

/**
 * Active-period PIX ceiling for the home bar.
 * Prefer Pix enviado (type 1); else first active outbound-enforced category.
 */
const pickActivePixLimit = (items = []) => {
  const active = items.filter(
    (item) =>
      item
      && item.activeNow
      && !item.comingSoon
      && Number(item.totalCents) > 0,
  );
  if (active.length === 0) return null;

  const byCode = (code, typeNum) =>
    active.find(
      (item) =>
        item.type === typeNum
        || item.typeCode === code
        || item.type_code === code
        || item.id === code,
    );

  return (
    byCode("enviado", 1)
    || byCode("pj", 4)
    || active[0]
  );
};

const MetricLabel = ({ label, tooltip, compact = false }) => (
  <div
    className={
      compact
        ? "home-balance-wallet__metric-label"
        : "home-balance-wallet__primary-title flex min-w-0 flex-1 items-center gap-1"
    }
  >
    <span className="min-w-0 truncate" title={label}>
      {label}
    </span>
    {tooltip ? (
      <Tooltip title={tooltip} className="tight valut-tooltip">
        <button
          type="button"
          className="home-balance-wallet__info-btn"
          aria-label={tooltip}
        >
          <TbInfoCircle className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
        </button>
      </Tooltip>
    ) : null}
  </div>
);

export const HomeBalanceCards = ({
  accountBalanceData,
  balanceValue: _balanceValue,
  loadingRefresh,
  onRefresh,
}) => {
  const [showBalance, setShowBalance] = useState(true);
  const [pixLimit, setPixLimit] = useState(null);
  const { hasPermission } = useAuth();

  const handleToggle = useCallback(() => {
    setShowBalance((prev) => !prev);
  }, []);

  const loadPixLimits = useCallback(async () => {
    try {
      const result = await getPixTransactionalLimits();
      setPixLimit(pickActivePixLimit(result?.items || []));
    } catch {
      setPixLimit(null);
    }
  }, []);

  useEffect(() => {
    loadPixLimits();
    const onActiveAccountChanged = () => {
      loadPixLimits();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    return () => {
      window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    };
  }, [loadPixLimits]);

  const handleRefresh = useCallback(() => {
    onRefresh?.();
    loadPixLimits();
  }, [onRefresh, loadPixLimits]);

  if (
    !hasPermission("statementsbalanceview")
    && !hasPermission("statementsview")
  ) {
    return null;
  }

  const masked = "••••••••";
  const availableCents = Number.isFinite(accountBalanceData?.availableCents)
    ? accountBalanceData.availableCents
    : 0;
  const blockedCents = Number.isFinite(accountBalanceData?.blockedCents)
    ? accountBalanceData.blockedCents
    : 0;
  const futureCents = Number.isFinite(accountBalanceData?.futureCents)
    ? accountBalanceData.futureCents
    : 0;
  const summedCents = availableCents + blockedCents + futureCents;
  const fromApi = Number.isFinite(accountBalanceData?.consolidatedCents)
    ? accountBalanceData.consolidatedCents
    : summedCents;
  // Never let Total ignore blocked/future when the API sent available-only.
  const consolidatedCents = Math.max(fromApi, summedCents);

  const money = (cents) =>
    displayMoney(showBalance, masked, formatCurrency(Number.isFinite(cents) ? cents : 0));

  // Over Balance ceiling — never confuse with Saldo disponível (availableCents).
  const inferredHeadroom = Math.max(
    0,
    Number(accountBalanceData?.spendableCents || 0)
      - Math.max(0, Number(accountBalanceData?.availableCents || 0)),
  );
  const limitCents = Math.max(
    0,
    Number(accountBalanceData?.limitCents) || 0,
    Number(accountBalanceData?.limit_cents) || 0,
    inferredHeadroom,
  );
  const showLimit =
    limitCents > 0
    || (accountBalanceData?.showLimit === true && inferredHeadroom > 0);

  // Liberado = unused slice of the ceiling — NOT Saldo disponível.
  // When API sends spendable === available, remaining often arrives as 0; treat full
  // teto as liberado while cash is still non-negative (not drawn into overdraft).
  const rawRemaining = accountBalanceData?.remainingLimitCents;
  const remainingLooksOmitted =
    Number.isFinite(rawRemaining)
    && rawRemaining === 0
    && limitCents > 0
    && availableCents >= 0
    && inferredHeadroom === 0;
  const remainingLimitCents = Math.min(
    limitCents,
    Math.max(
      0,
      remainingLooksOmitted
        ? limitCents
        : Number.isFinite(rawRemaining)
          ? rawRemaining
          : inferredHeadroom || limitCents,
    ),
  );

  const secondaryMetrics = [
    {
      key: "blocked",
      label: intl.get("baas.processing.blocked") || intl.get("extract.blockedBalance"),
      tooltip: intl.get("extract.blockedBalanceTooltip"),
      value: money(blockedCents),
      tone: "blocked",
    },
    {
      key: "future",
      label: intl.get("baas.processing.future") || intl.get("extract.pendingBalance"),
      tooltip: intl.get("extract.pendingBalanceTooltip"),
      value: money(futureCents),
      tone: "future",
    },
  ];

  if (showLimit) {
    secondaryMetrics.push(
      {
        key: "limit-available",
        label: intl.get("extract.availableLimit"),
        tooltip:
          intl.get("extract.availableLimitTooltip")
          || intl.get("extract.overdraftLimitTooltip"),
        value: money(remainingLimitCents),
        tone: "limitAvailable",
      },
      {
        key: "limit",
        label: intl.get("extract.configuredLimit") || intl.get("extract.limitBalance"),
        tooltip:
          intl.get("extract.configuredLimitTooltip")
          || intl.get("extract.overdraftLimitTooltip"),
        value: money(limitCents),
        tone: "limit",
      },
    );
  }

  secondaryMetrics.push({
    key: "total",
    label: intl.get("extract.totalConsolidatedBalance"),
    tooltip: intl.get("extract.totalConsolidatedBalanceTooltip"),
    value: money(consolidatedCents),
    tone: "total",
  });

  const pixTotalCents = Number(pixLimit?.totalCents) || 0;
  const pixUsedCents = Math.max(0, Number(pixLimit?.usedCents) || 0);
  const showPixLimit = pixTotalCents > 0;
  const pixProgress = resolveLimitProgress(pixUsedCents, pixTotalCents);
  const usedPct = pixProgress.barPercent;
  const pixLimitLabel =
    pixLimit?.period === "noturno"
      ? (intl.get("extract.pixLimitNightLabel") || "Limite da noite")
      : (intl.get("extract.pixLimitDayLabel") || "Limite do dia");
  const pixOfCeiling =
    intl.get("extract.pixLimitOfCeiling") || intl.get("extract.overdraftOfCeiling") || "de";
  const pixLimitTooltip =
    intl.get("extract.pixLimitTooltip")
    || "Limite PIX da conta no período ativo (usado × disponível / teto).";

  return (
    <section className="mb-6 min-w-0 w-full">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="dashboard-section-label">
          {intl.get("extract.balances")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {showBalance && (
            <button
              type="button"
              onClick={handleRefresh}
              className="grid-history-action-btn grid-history-action-btn--icon"
              aria-label={intl.get("common.refreshBalance")}
            >
              {loadingRefresh?.GET ? (
                <CircularProgress size={14} />
              ) : (
                <TbRefresh className="h-4 w-4" />
              )}
            </button>
          )}
          <button type="button" onClick={handleToggle} className="grid-history-action-btn">
            {showBalance ? <TbEye className="h-3.5 w-3.5" /> : <TbEyeOff className="h-3.5 w-3.5" />}
            {showBalance ? intl.get("common.hideBalance") : intl.get("common.showBalance")}
          </button>
        </div>
      </div>

      <article className="home-balance-wallet summary-card">
        <div className="home-balance-wallet__primary">
          <div className="home-balance-wallet__primary-head">
            <div
              className={`home-balance-wallet__icon ${ICON_TONE_CLASS.available}`}
              aria-hidden
            >
              <TbBuildingBank className="h-5 w-5" />
            </div>
            <p className="home-balance-wallet__primary-title">
              {intl.get("extract.availableBalance")}
            </p>
          </div>
          <p className="home-balance-wallet__primary-value">{money(availableCents)}</p>
        </div>

        <div
          className={
            showLimit
              ? "home-balance-wallet__metrics home-balance-wallet__metrics--with-limit"
              : "home-balance-wallet__metrics"
          }
          role="list"
        >
          {secondaryMetrics.map((metric) => (
            <div
              key={metric.key}
              className={`home-balance-wallet__metric home-balance-wallet__metric--${metric.tone}`}
              role="listitem"
            >
              {/* Same composition as Saldo disponível: icon+label row, then value — smaller. */}
              <div className="home-balance-wallet__metric-head">
                <div
                  className={`home-balance-wallet__icon home-balance-wallet__icon--sm ${ICON_TONE_CLASS[metric.tone] || ""}`}
                  aria-hidden
                >
                  <TbBuildingBank className="h-3.5 w-3.5" />
                </div>
                <MetricLabel label={metric.label} tooltip={metric.tooltip} compact />
              </div>
              <p className="home-balance-wallet__metric-value">{metric.value}</p>
            </div>
          ))}
        </div>
      </article>

      {showPixLimit ? (
        <div className="home-balance-pix-limit" data-testid="home-balance-pix-limit">
          <div className="home-balance-pix-limit__row">
            <Tooltip title={pixLimitTooltip} className="tight valut-tooltip">
              <p className="home-balance-pix-limit__label">{pixLimitLabel}</p>
            </Tooltip>
            <p className="home-balance-pix-limit__amounts">
              <span className="home-balance-pix-limit__used">{money(pixUsedCents)}</span>
              <span className="home-balance-pix-limit__of">
                {" "}
                {pixOfCeiling} {money(pixTotalCents)}
              </span>
            </p>
          </div>

          <div
            className="home-balance-pix-limit__track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={pixTotalCents}
            aria-valuenow={pixUsedCents}
            aria-label={pixLimitLabel}
          >
            <span
              className="home-balance-pix-limit__fill"
              style={{ width: `${usedPct}%` }}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};
